<template>
  <base-slide-over size="4xl" ref="slideOver" title="Create Prescription">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <div>Patient Information</div>
      <base-email-field class="tw-mt-3" label="Email" placeholder="Enter email" v-model="email" required @blur="checkIfEmailExists" />
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4">
        <base-text-field label="Legal First and Last Name" placeholder="Enter legal first and last name" v-model="fullName" required />
        <base-text-field label="Nick Name" placeholder="Enter nick name" v-model="nickName" />
        <base-phone-number-input label="Phone" placeholder="Enter phone" v-model="mobile" :e164.sync="phone164" required />
      </div>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4"></div>

      <div :class="`tw-grid tw-grid-cols-1 sm:tw-grid-cols-${organization.collectDobSetting ? '2' : '1'} tw-gap-x-4 tw-mt-4`">
        <base-date-picker label="Date of Birth" placeholder="Enter date of birth" initial-view="year" v-model="dob" required v-if="organization.collectDobSetting" />
        <base-select label="Gender" :options="genderOptions" placeholder="Select gender" label-key="name" v-model="gender" required />
      </div>
      <div :class="`tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4`">
        <base-date-picker label="Last Appointment" placeholder="Select last appointment date" v-model="lastAppointmentDate" required />
        <base-select
          label="Appointment Location"
          :options="appointmentLocationOptions"
          placeholder="Select the setting of the last appointment"
          label-key="name"
          v-model="appointmentLocation"
          required
        />
      </div>
      <base-textarea label="Known Allergies" placeholder="Enter known allergies" v-model="knownAllergies" />
      <div v-if="organization.collectMailingAddressSetting" class="tw-mt-7">
        <div class="tw-flex tw-flex-col">
          <div>
            Mailing Address
            <span v-if="patientExists" class="tw-text-red-600 tw-text-center tw-mt-2 tw-text-sm">
              (User with same email already exists. Pre-populating the address on file)
            </span>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-3">
          <base-text-field class="tw-col-span-2" label="Line 1 (optional)" placeholder="Enter line 1" v-model="mailingAddress.line1" />
          <base-text-field label="Line 2 (optional)" placeholder="Enter line 2" v-model="mailingAddress.line2" />
        </div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-5">
          <base-text-field label="City (optional)" placeholder="Enter city" v-model="mailingAddress.city" />
          <base-select label="State (optional)" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="mailingAddress.state" />
          <base-text-field label="Zip Code (optional)" placeholder="Enter zip code" v-model="mailingAddress.postalCode" />
        </div>
      </div>
      <base-select
        label="Practice Location"
        placeholder="Select a location"
        :optgroups="locationOptGroups"
        v-model="selectedLocationId"
        label-key="name"
        value-key="businessLocationId"
        required
        v-if="locations.length > 1"
        class="tw-mt-6"
      />
      <div class="tw-mt-2 tw-mb-3">
        Prescription Programs <small class="tw-text-gray-600" v-if="locations.length > 1">(Please select practice location to load programs)</small>
      </div>

      <FilterProgramComponent
        v-for="(prescription, index) in prescriptions"
        :key="`program-${index}`"
        :programs="sortedPrograms"
        :programGroups="programGroups"
        v-model="prescriptions[index]"
        :class="['tw-mb-1', { 'tw-border-t tw-pt-6 tw-mb-6': index > 0 }]"
        :required="!prescriptions.find((p) => !!p.businessProgramId) && index === 0"
        :allRxItems="rxItems"
        ref="prescriptionInput"
      />

      <div class="tw-flex tw-justify-center tw-py-2">
        <base-button @click="addAnotherProgram" type="flat">Add Another Program</base-button>
      </div>
    </base-form>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" @click="$refs.slideOver.hide()" class="tw-mr-3">Cancel</base-button>
        <base-button @click="createPrescription" :disabled="!isFormValid" :loading="isCreating">Create</base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import FilterProgramComponent from "@/views/business/components/FilterPrescriptionInput";
import { getClinicalJustificationValidationError } from "@/utils/clinicalJustification";
import { orderBy } from "lodash";
import { STATE_OPTIONS } from "@/constants/constants";
import { mapGetters } from "vuex";

export default {
  components: {
    FilterProgramComponent,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isCreating: false,
      isFormValid: true,
      fullName: "",
      email: "",
      mobile: "",
      phone164: "",
      dob: "",
      gender: null,
      genderOptions: [{ value: "male", name: "Male" }, { value: "female", name: "Female" }],
      appointmentLocation: null,
      appointmentLocationOptions: [{ value: "office", name: "In Office Visit" }, { value: "virtual", name: "Virtual Visit" }],
      lastAppointmentDate: null,
      knownAllergies: "",
      STATE_OPTIONS,
      mailingAddress: {
        line1: null,
        line2: null,
        city: null,
        state: null,
        postalCode: null,
      },
      selectedLocationId: null,
      prescriptions: [{ businessProgramId: null, medications: [], notes: null, rxItems: [] }],
      programs: [],
      programGroups: [],
      rxItems: [],
      patientExists: false,

      filteredPrograms: [],
      nickName: "",
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["location", "locations"]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered"]),
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: orderBy(this.locations.filter((l) => l.state === s.value), ["name"]),
      }));
    },
    sortedPrograms() {
      // This computation prepares programs for `FilterProgramComponent`
      if (!this.programGroups.length) return [];

      return orderBy(
        this.programGroups
          .filter((pg) => this.filteredPrograms.some((p) => p.programGroup === pg)) // Ensure group has programs after filtering
          .map((pg) => ({
            label: pg ? pg : "No Group",
            options: orderBy(
              this.filteredPrograms
                .filter((p) => p.programGroup === pg)
                .map((p) => {
                  const priceString = p.price && typeof p.price.cents === "number" ? `$${p.price.cents / 100}` : "Price N/A";
                  return {
                    ...p,
                    name: `${p.name} - ${priceString}`,
                  };
                }),
              ["name"],
            ),
          })),
        ["label"],
      );
    },
  },
  created() {
    // Fetch all necessary data when the slideover is created
    this.getProgramGroups();
    this.getRxItems();
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.$refs.form.reset();
      this.patientExists = false;
      if (this.locations.length > 1) {
        this.filteredPrograms = [];
      }

      // Reset all form fields and prescriptions
      this.fullName = "";
      this.email = "";
      this.mobile = "";
      this.phone164 = "";
      this.dob = "";
      this.gender = null;
      this.appointmentLocation = null;
      this.lastAppointmentDate = null;
      this.knownAllergies = "";
      this.mailingAddress = { line1: null, line2: null, city: null, state: null, postalCode: null };
      this.nickName = "";
      this.prescriptions = [{ businessProgramId: null, medications: [], notes: null, rxItems: [] }];
    },
    hide() {
      this.$refs.slideOver.hide();
    },
    async getPrograms() {
      if (!this.selectedLocationId) {
        console.warn("selectedLocationId is not set. Cannot fetch programs.");
        return;
      }
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/${this.selectedLocationId}/programs/list?includeDeleted=false`);
        this.programs = response.data;

        if (this.isLocationFiltered && Array.isArray(this.locations) && this.locations.length > 0) {
          const selectedLocationIds = this.locations.map((loc) => String(loc.businessLocationId));
          this.filteredPrograms = this.programs.filter((program) => {
            const locationFilterType = program.locationFilterType;
            let programLocationIds = [];
            try {
              const parsed = JSON.parse(program.locationsId || "[]");
              programLocationIds = Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
            } catch (e) {
              programLocationIds = [];
            }

            if (locationFilterType === "all") {
              return true;
            }
            if (locationFilterType === "include") {
              return programLocationIds.some((id) => selectedLocationIds.includes(id));
            }
            if (locationFilterType === "exclude") {
              return !programLocationIds.some((id) => selectedLocationIds.includes(id));
            }
            return false;
          });
        } else {
          this.filteredPrograms = this.programs; // No location filtering applied
        }
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve programs",
          message: "We were not able to retrieve your business programs for the selected location. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async getProgramGroups() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/groups`);
        this.programGroups = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve program groups",
          message: "We were not able to retrieve your business program groups. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async getRxItems() {
      try {
        const url = `/businesses/${this.businessId}/item-categories/all`;
        this.$axios.get(url).then((res) => {
          this.rxItems = res.data.filter((i) => i.type === "rx_item" && i.status === "active" && i.isDeleted === false);
        });
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve Rx Items",
          message: "We were not able to retrieve your Rx Items. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async createPrescription() {
      // Validate the main form fields
      if (!this.$refs.form.validate()) {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Validation Error", message: "Please fill in all required patient fields." });
        return;
      }

      // Validate at least one program or Rx item is selected across all prescriptions
      const hasContent = this.prescriptions.some((p) => !!p.businessProgramId);
      if (!hasContent) {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Validation Error", message: "Please select at least one program" });
        return;
      }

      const clinicalJustificationError = getClinicalJustificationValidationError(this.prescriptions, this.filteredPrograms);
      console.log("Clinical Justification Validation Result:", clinicalJustificationError);
      if (clinicalJustificationError) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Clinical Justification Required",
          message: clinicalJustificationError.message,
          error: clinicalJustificationError.error,
        });
        return;
      }

      try {
        this.isCreating = true;

        // Prepare the prescriptions payload
        const prescriptionsPayload = this.prescriptions
          .filter((p) => !!p.businessProgramId || (p.rxItems && p.rxItems.length > 0)) // Only send prescriptions that have content
          .map((p) => ({
            ...p,
            businessLocationId: this.selectedLocationId, // Ensure location is added to each prescription
            medications: p.medications
              .filter((m) => !!m) // Filter out null/undefined medications
              .map((m, i) => ({
                businessItemCategoryId: m,
                sortOrder: i,
              })),
            rxItems: p.rxItems
              .filter((item) => !!item) // Filter out null/undefined rxItems
              .map((item, i) => ({
                businessItemCategoryId: item.businessItemCategoryId,
                sortOrder: i,
              })),
            notes: p.notes ? [p.notes] : [], // Ensure notes are an array, or empty if null
          }));
        await this.$axios.post(`/businesses/${this.businessId}/prescriptions`, {
          businessLocationId: this.selectedLocationId,
          user: {
            fullName: this.fullName,
            email: this.email,
            mobilePhone: this.phone164,
            mailingAddressLine1: this.mailingAddress.line1,
            mailingAddressLine2: this.mailingAddress.line2,
            mailingAddressCity: this.mailingAddress.city,
            mailingAddressState: this.mailingAddress.state,
            mailingAddressPostalCode: this.mailingAddress.postalCode,
            dob: this.dob,
            gender: this.gender,
            knownAllergies: this.knownAllergies,
            lastAppointmentDate: this.lastAppointmentDate,
            appointmentLocation: this.appointmentLocation,
            nickName: this.nickName,
          },
          prescriptions: prescriptionsPayload,
        });

        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Prescription Created", message: "The prescription has been submitted successfully." });
        this.hide();
        this.$emit("created"); // Notify parent that creation was successful
      } catch (error) {
        // More robust error handling
        if (error.response && error.response.data) {
          const errorMessage =
            Array.isArray(error.response.data) && error.response.data.length > 0 ? error.response.data[0].message : error.response.data.message || "An unexpected error occurred.";

          if (errorMessage.includes("User already has prescriptions for this program")) {
            this.EventBus.$emit("GLOBAL_ERROR", {
              title: "Patient already has active plan for this program",
              message: `If you are attempting to refill a prescription for this patient, please go to Activities to manage your refill authorizations.`,
              error,
            });
          } else {
            this.EventBus.$emit("GLOBAL_ERROR", {
              title: "Failed to create prescription",
              message: `${errorMessage} Please try again.`,
              error,
            });
          }
        } else {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to create prescription",
            message: `We were not able to create the prescription for ${this.fullName}. Please try again.`,
            error,
          });
        }
      } finally {
        this.isCreating = false;
      }
    },
    addAnotherProgram() {
      // Add a new empty prescription object, ensuring all expected properties are initialized
      this.prescriptions.push({
        businessProgramId: null,
        medications: [],
        notes: null,
        rxItems: [], // Critical: Initialize rxItems for new prescription entries
      });
    },
    async checkIfEmailExists() {
      if (!this.email) return;

      try {
        const response = await this.$axios.get(`/users/search?email=${encodeURIComponent(this.email)}`);

        if (response.data && response.data.metadata && response.data.metadata.isSuccessful) {
          this.patientExists = true;

          const patient = response.data;
          if (patient) {
            //set patient info
            this.mobile = patient.phoneNumber;
            this.dob = patient.dob;
            this.gender = patient.gender;

            if (!this.fullName) {
              this.fullName = patient.fullName;
            }

            //set mailing address
            if (patient.mailingAddress) {
              this.mailingAddress = {
                line1: patient.mailingAddress.line1,
                line2: patient.mailingAddress.line2,
                city: patient.mailingAddress.city,
                state: patient.mailingAddress.state,
                postalCode: patient.mailingAddress.zip,
              };
            }
            //set nick name
            if (patient.nickName) {
              this.nickName = patient.nickName;
            }
          }

          // this.foundPatient = {
          //   userId: response.data.userId,
          //   emailAddress: response.data.emailAddress,
          // };
          // this.isFormValid = false;
        } else {
          this.patientExists = false;
          // this.foundPatient = null;
          //   this.isFormValid = true;
        }
      } catch (error) {
        console.error("Error checking email:", error);
        this.patientExists = false;
        // this.foundPatient = null;
        // this.isFormValid = true;
      }
    },
  },
  watch: {
    locations: {
      immediate: true,
      handler() {
        if (this.locations.length === 1) {
          this.selectedLocationId = this.locations[0].businessLocationId;
        }
      },
    },
    selectedLocationId: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          if (!oldVal) {
            //wait for 500ms to ensure the location is set before fetching programs
            setTimeout(() => {
              this.getPrograms();
            }, 500);
          } else {
            this.getPrograms();
          }
          this.prescriptions = [{ businessProgramId: null, medications: [], notes: null }];
        } else if (newVal && oldVal === undefined) {
          setTimeout(() => {
            this.getPrograms();
          }, 500);
        }
      },
    },
  },
};
</script>
